import { GeocodingClient } from '@at.govt.nz/geocoder-sdk';
import GenerateAddress from '../transforms/GenerateAddress';
import GenerateDisplayText from '../transforms/GenerateDisplayText';
import { ADDRESS_CATEGORIES } from '../constants/addressCategories';
import { recordException } from './AnalyticsService';
import AliasMacronService from './AliasMacronService';

let mapboxGeocodingClient;
const GEOCODER_MIN_QUERY_LENGTH = process.env.REACT_APP_GEOCODER_MIN_QUERY_LENGTH || 3; // minimum query length for forward geocoding

const updateMapCategory = (oldCategory) => {
  const key = oldCategory?.toUpperCase();

  switch (key) {
    case 'BUS_STOP':
      return ADDRESS_CATEGORIES.BUSSTOP;
    case 'TRAIN_STATION':
      return ADDRESS_CATEGORIES.TRAINSTATION;
    case 'FERRY_TERMINAL':
      return ADDRESS_CATEGORIES.FERRYTERMINAL;
    default:
      return oldCategory;
  }
};

const processLocation = ({
  geometry: { coordinates }, properties: {
    category, name, stopCode, address, type,
  },
}) => ({
  name,
  displayText: GenerateDisplayText(name, type, stopCode, address),
  address: GenerateAddress(name, type, stopCode, address),
  category: updateMapCategory(category),
  lng: coordinates[0],
  lat: coordinates[1],
  type,
});

const deduplicateByStopCode = (locations) => {
  const seen = new Set();

  return locations.filter((feature) => {
    const stopCode = feature?.properties?.stopCode;

    if (stopCode) {
      if (seen.has(stopCode)) return false;
      seen.add(stopCode);
    }

    return true;
  });
};

const forwardGeocode = async (query, currentPosition) => {
  const trimmedQuery = query?.trim() ?? '';
  if (trimmedQuery.length < GEOCODER_MIN_QUERY_LENGTH) return [];

  try {
    const proximity = currentPosition ? [currentPosition.longitude, currentPosition.latitude] : undefined;
    const matchingAlias = AliasMacronService.getAlias(query);
    const aliasPromise = matchingAlias
      ? mapboxGeocodingClient?.forwardGeocodeGtfs({ query: matchingAlias, proximity })
      : Promise.resolve(null);

    const mainPromise =
      mapboxGeocodingClient?.forwardGeocode({ query: trimmedQuery, proximity }) ?? Promise.resolve(null);

    const [aliasResult, geocodeResult] = await Promise.all([aliasPromise, mainPromise]);

    let locations = [];
    if (aliasResult?.features && !aliasResult?.errors?.length) {
      locations = aliasResult.features;
    }

    if (geocodeResult?.errors?.length) {
      const description = `Geocoder forwardGeocode partial errors: ${geocodeResult.errors.join('; ')}`;
      recordException({ description });
    }

    if (geocodeResult?.features) {
      locations = [...locations, ...geocodeResult.features];
    }

    return deduplicateByStopCode(locations).map(processLocation);
  } catch (error) {
    recordException({ description: `Geocoder forwardGeocode error: ${error.message}` });
    return [];
  }
};

const reverseGeocode = async (latitude, longitude) => {
  try {
    const geocodeResult = await mapboxGeocodingClient?.reverseGeocode([longitude, latitude]);

    if (geocodeResult?.errors?.length) {
      throw new Error(`Reverse geocode error: ${geocodeResult.errors.join('; ')}`);
    }

    if (geocodeResult?.features?.length > 0) {
      return processLocation(geocodeResult.features[0]);
    }

    return undefined;
  } catch (error) {
    recordException({ description: `Geocoder reverseGeocode error: ${error.message}` });
    throw error;
  }
};

/**
 * Function used in unit test to reset the client between tests
 */
const clearInstance = () => {
  mapboxGeocodingClient = null;
}

const getInstance = () => {
  if (!mapboxGeocodingClient) {
    const FORWARD_GEOCODE_LIMIT_GTFS = { station: 10, others: 5, total: 10 }; // limit of GTFS results
    const FORWARD_GEOCODE_LIMIT_MAPBOX = 10; // limit of Mapbox results
    const cdnBaseUrl = process.env.REACT_APP_CDN_BASE_URL;

    mapboxGeocodingClient = new GeocodingClient({
      cdnBaseUrl,
      gtfs: {
        apiBaseUrl: process.env.REACT_APP_AT_API_URL,
        apiKey: process.env.REACT_APP_AT_API_KEY,
      },
      limit: {
        gtfs: FORWARD_GEOCODE_LIMIT_GTFS,
        mapbox: FORWARD_GEOCODE_LIMIT_MAPBOX,
      },
      mapbox: {
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN,
      },
    });
  }

  return {
    forwardGeocode,
    reverseGeocode,
  };
};

const GeocoderService = {
  GEOCODER_MIN_QUERY_LENGTH,
  clearInstance,
  getInstance,
  updateMapCategory,
};
export default GeocoderService;
